import { GanttTask } from '../types/gantt.types'
import { useCallback, useEffect } from 'react'
import { getForemanConfirmRequest, ScheduleChangeNoticeItemResult } from '../api/foremanApi'
import { SigongAssignUpdate } from '../views/SigongPopup'
import { mergeForemanAssigned, partnerStatusAfterSend, partnerStatusAfterChange } from '../constants/partnerStatus'
import { PartnerCardSent, PartnerCardChanged } from '../components/PartnerCard'
import { noticeSentPatch } from '../components/ScheduleChangeNoticeModal'
// 시공반장·파트너 국소 갱신(반장 배정·일정확인 발송·확정 반장 변경·변경 카톡 발송) — 이동 전 GanttView.tsx 본문을 그대로 옮긴 것이다.

export type UseGanttPartnerPatchArgs = {
  setTasks: React.Dispatch<React.SetStateAction<GanttTask[]>>
  originalTasksRef: React.MutableRefObject<GanttTask[]>
  setPanelForeman: any
  setPanelPartnerId: any
  setPanelNotice: any
  panelNotice: any
}

export function useGanttPartnerPatch({
  setTasks, originalTasksRef, setPanelForeman, setPanelPartnerId, setPanelNotice, panelNotice,
}: UseGanttPartnerPatchArgs) {
  // 시공반장 팝업(SigongPopup)에서 배정/발송이 성공했을 때의 국소 갱신 — 화면 전체를
  // 다시 불러오면(refreshKey) 뒤 화면이 새로고침되는 것처럼 보여서, 바뀐 taskId 한 건만
  // tasks 상태에 반영한다.
  // 발송·변경 성공 직후 한 공종의 일정확인 요청 요약(rank·previousOutcome 포함)만 단건 API로 다시 받아 반영한다
  // (전체 재조회 금지 — 뒤 화면 새로고침 방지). 실패하면 낙관적 임시값을 그대로 둔다.
  const refreshConfirmRequest = useCallback(async (taskId: number) => {
    try {
      const cr = await getForemanConfirmRequest(taskId)
      const apply = <T extends { taskId: number }>(list: T[]): T[] => list.map(t => t.taskId === taskId ? { ...t, confirmRequest: cr } : t)
      setTasks(prev => apply(prev))
      originalTasksRef.current = apply(originalTasksRef.current)
    } catch { /* 임시값 유지 */ }
  }, [])

  const handleForemanAssigned = useCallback((u: SigongAssignUpdate) => {
    setTasks(prev => prev.map(t => t.taskId === u.taskId
      ? mergeForemanAssigned({ ...t, foremanName: u.foremanName, foremanContact: u.foremanContact }, u)
      : t))
    if (u.sentDtm) void refreshConfirmRequest(u.taskId)
  }, [refreshConfirmRequest])

  // 상세창 시공파트너 카드에서 일정확인 카톡 발송이 성공했을 때 — 서버 응답(partnerStatus·sentDtm)으로
  // 이 공종 한 건만 국소 갱신한다(막대·행의 상태 표시와 일치).
  const handlePartnerSent = useCallback((sent: PartnerCardSent) => {
    const { result, partnerId, name, mobile } = sent
    setTasks(prev => prev.map(t => t.taskId === result.taskId ? {
      ...t,
      foremanName:       name || t.foremanName,
      foremanContact:    mobile ?? t.foremanContact,
      assigneePartnerId: partnerId,
      assignSource:      partnerId !== t.assigneePartnerId ? 'MANUAL' : t.assignSource,
      ...partnerStatusAfterSend(result.sentDtm, result.partnerStatus, {
        historyId: result.historyId, attemptNo: result.attemptNo, sendMethod: result.sendMethod, partnerId: result.partnerId, replyExpireDtm: result.replyExpireDtm,
      }),
    } : t))
    void refreshConfirmRequest(result.taskId)
    setPanelForeman(name)
    setPanelPartnerId(partnerId)
    setPanelNotice(`${name} 반장님께 일정확인 카톡을 보냈어요`)
  }, [refreshConfirmRequest])

  // 상세창 시공파트너 카드에서 확정 반장 변경 + 바로 보내기가 성공했을 때 — 응답으로 이 공종 한 건만 국소 갱신한다.
  const handlePartnerChanged = useCallback((changed: PartnerCardChanged) => {
    const { result, name, mobile, message } = changed
    setTasks(prev => prev.map(t => t.taskId === result.taskId ? {
      ...t,
      foremanName:       name || t.foremanName,
      foremanContact:    mobile ?? t.foremanContact,
      assigneePartnerId: result.partnerId,
      assignSource:      'MANUAL',
      ...partnerStatusAfterChange(result),
    } : t))
    if (result.sent) void refreshConfirmRequest(result.taskId)
    setPanelForeman(name)
    setPanelPartnerId(result.partnerId)
    setPanelNotice(message)
  }, [refreshConfirmRequest])

  useEffect(() => {
    if (!panelNotice) return
    const timer = setTimeout(() => setPanelNotice(''), 6000)
    return () => clearTimeout(timer)
  }, [panelNotice])

  // 변경 카톡 확인 창에서 발송이 성공한 공종 — 서버 응답(partnerStatus·sentDtm·replyExpireDtm)으로 이 공종들만 국소 갱신.
  const handleChangeNoticeSent = useCallback((sentItems: ScheduleChangeNoticeItemResult[]) => {
    const patches = new Map<number, ReturnType<typeof noticeSentPatch>>()
    for (const it of sentItems) patches.set(it.taskId, noticeSentPatch(it))
    const merge = <T extends { taskId: number }>(list: T[]): T[] =>
      list.map(t => { const p = patches.get(t.taskId); return p ? { ...t, ...p } : t })
    setTasks(prev => merge(prev))
    originalTasksRef.current = merge(originalTasksRef.current)   // [취소]가 발송 상태를 되돌리지 않게
    for (const it of sentItems) if (it.sent && it.sentDtm) void refreshConfirmRequest(it.taskId)
  }, [refreshConfirmRequest])

  return { handlePartnerSent, handlePartnerChanged, handleForemanAssigned, handleChangeNoticeSent }
}
